import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

import { OperationService } from '../../../services';
import type { BlurType, OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_DEBLUR_STATE,
  buildDeblurRequest,
  hasSourceImage,
  type DeblurFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  onResult?: (result: OperationResult) => void;
}

const BLUR_OPTIONS: Array<{ value: BlurType; label: string }> = [
  { value: 'Auto', label: 'Auto-detect' },
  { value: 'Motion', label: 'Motion blur' },
  { value: 'Focus', label: 'Focus blur' },
];

export const DeblurPanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  onResult,
}) => {
  const [state, setState] = useState<DeblurFormState>(DEFAULT_DEBLUR_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.deblur);

  const hasSource = hasSourceImage(source);

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to deblur.');
      return;
    }
    try {
      const sid = await ensureSession();
      const request = buildDeblurRequest(state, sid, source);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled = mutation.isPending || isCreatingSession || !hasSource;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TuneIcon fontSize="small" />
        <Typography variant="subtitle2">Deblur</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to deblur.</Alert>
      )}

      <FormControl size="small" fullWidth>
        <InputLabel id="deblur-type-label">Blur type</InputLabel>
        <Select
          labelId="deblur-type-label"
          label="Blur type"
          value={state.blurType}
          onChange={e => setState(s => ({ ...s, blurType: e.target.value as BlurType }))}
        >
          {BLUR_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Typography variant="caption" color="text.secondary">
          Strength · {state.strength.toFixed(2)}
        </Typography>
        <Slider
          value={state.strength}
          onChange={(_, v) => setState(s => ({ ...s, strength: Array.isArray(v) ? v[0] : v }))}
          min={0}
          max={1}
          step={0.05}
          size="small"
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={state.preserveDetails}
            onChange={e => setState(s => ({ ...s, preserveDetails: e.target.checked }))}
            size="small"
          />
        }
        label="Preserve fine details"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Deblur failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <TuneIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Deblurring…' : 'Deblur'}
        </Button>
      </Box>
    </Stack>
  );
};
